import { useGetContractPreview } from '@/lib/api/risk/brokerage/tt/staged';
import { useListLocations, useListMarketZones } from '@/lib/api/generalContract';
import { useListCustomers } from '@/lib/api/customer';
import { useListTraders } from '@/lib/api/trader';
import { ContractPreviewModal, previewState } from '@/section/contracts/shared/GeneralContractModals';
import { Loader } from '@/components/ui/loader';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface TTContractPreviewModalProps {
  eventId: string;
  guidOrderId: string;
  onClose: () => void;
}

export function TTContractPreviewModal({
  eventId,
  guidOrderId,
  onClose,
}: TTContractPreviewModalProps) {
  const previewClearForm: () => void = previewState.useInputsState(
    (state: { clearForm: () => void }) => state.clearForm
  );

  // Fetch all required data
  const locationsResult = useListLocations();
  const marketZonesResult = useListMarketZones();
  const customersResult = useListCustomers({ minimal: true, hasRiskProfile: true });
  const tradersResult = useListTraders();
  const contractPreviewResult = useGetContractPreview(eventId, guidOrderId);

  const locations = locationsResult.data?.data ?? [];
  const marketZones = marketZonesResult.data?.data ?? [];
  const customers = customersResult.data?.data ?? [];
  const traders = tradersResult.data?.data ?? [];

  const isLoading =
    locationsResult.isLoading ||
    marketZonesResult.isLoading ||
    customersResult.isLoading ||
    tradersResult.isLoading ||
    contractPreviewResult.isLoading;

  const isError = contractPreviewResult.isError;

  // Get the contract preview data to pass to the modal
  const contractPreviewData = contractPreviewResult.data?.data?.data as Record<string, unknown> | undefined;

  const handleClose = () => {
    previewClearForm();
    onClose();
  };

  if (isLoading) {
    return (
      <Dialog open onOpenChange={(value) => !value && handleClose()}>
        <DialogContent className="max-w-[98%] h-[98%] flex flex-col">
          <DialogHeader>
            <DialogTitle>Contract Preview (Read Only)</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center flex-1">
            <Loader />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (isError) {
    return (
      <Dialog open onOpenChange={(value) => !value && handleClose()}>
        <DialogContent className="max-w-[98%] h-[98%] flex flex-col">
          <DialogHeader>
            <DialogTitle>Contract Preview (Read Only)</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center flex-1 gap-4">
            <p className="text-red-500">Error loading contract preview</p>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <ContractPreviewModal
      isOpen
      onClose={handleClose}
      locations={locations}
      marketZones={marketZones}
      customers={customers}
      traders={traders}
      initialData={contractPreviewData}
    />
  );
}
